import React, { useState } from 'react';
import { Siswa, Kelas } from '../../types/index.js';
import { Wallet, Search, ArrowDownCircle, ArrowUpCircle, Printer } from 'lucide-react';
import { useDataTable } from '../../hooks/useDataTable.js';
import { Pagination } from '../ui/Pagination.js';
import { SortableHeader } from '../ui/SortableHeader.js';
import { CetakBukuTabunganModal } from './CetakBukuTabunganModal.js';
import { useDialog } from '../ui/DialogProvider.js';

interface SaldoSiswaViewProps {
  siswaList: Siswa[];
  kelasList: Kelas[];
  onDepositOrWithdraw: (payload: {
    siswaId: string;
    tipe: 'SETOR' | 'TARIK';
    nominal: number;
    keterangan: string;
    sumberDana?: 'KASIR' | 'EWALLET';
  }) => Promise<void>;
}

export const SaldoSiswaView: React.FC<SaldoSiswaViewProps> = ({
  siswaList,
  kelasList,
  onDepositOrWithdraw,
}) => {
  const { showAlert } = useDialog();
  const [filterKelas, setFilterKelas] = useState('ALL');
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Siswa | null>(null);
  const [trxType, setTrxType] = useState<'SETOR' | 'TARIK'>('SETOR');
  const [targetWallet, setTargetWallet] = useState<'TABUNGAN' | 'EWALLET'>('TABUNGAN');
  const [sumberDana, setSumberDana] = useState<'KASIR' | 'EWALLET'>('KASIR');
  const [nominal, setNominal] = useState('');
  const [keterangan, setKeterangan] = useState('');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [studentToPrint, setStudentToPrint] = useState<Siswa | null>(null);

  const {
    searchQuery: search,
    setSearchQuery: setSearch,
    sortBy,
    sortDir,
    handleSort,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    totalRows,
    paginatedData: filteredSiswa,
    processedData
  } = useDataTable<Siswa>({
    data: siswaList,
    searchFields: ['nama', 'nis'],
    initialSortBy: 'nama',
    customSearchFn: (item, query) => {
      const matchKelas = filterKelas === 'ALL' || item.kelasNama === filterKelas;
      const matchSearch =
        !query ||
        item.nama.toLowerCase().includes(query) ||
        item.nis.includes(query);
      return matchKelas && matchSearch;
    }
  });

  const totalSaldoAll = processedData.reduce((sum, s) => sum + (s.saldoTabungan || 0), 0);

  const handleOpenAction = (s: Siswa, type: 'SETOR' | 'TARIK', wallet: 'TABUNGAN' | 'EWALLET' = 'TABUNGAN') => {
    setSelectedStudent(s);
    setTrxType(type);
    setTargetWallet(wallet);
    setSumberDana('KASIR');
    setNominal('');
    if (wallet === 'EWALLET') {
      setKeterangan(type === 'SETOR' ? 'Top Up Saldo E-Wallet' : 'Tarik Saldo E-Wallet');
    } else {
      setKeterangan(type === 'SETOR' ? 'Setoran Tabungan Siswa' : 'Penarikan Tabungan Siswa');
    }
    setShowModal(true);
  };

  const handleOpenPrint = (s: Siswa) => {
    setStudentToPrint(s);
    setShowPrintModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent || !nominal) return;

    const nom = Number(nominal);
    const saldo = targetWallet === 'TABUNGAN' ? (selectedStudent.saldoTabungan || 0) : (selectedStudent.saldoEWallet || 0);

    if (trxType === 'TARIK') {
      const isLimitEnabled = localStorage.getItem('tabungan_limitPenarikan') === 'true';
      const limitAmount = Number(localStorage.getItem('tabungan_limitPenarikanAmount')) || 100000;
      const isMinBalanceEnabled = localStorage.getItem('tabungan_minimumSaldo') === 'true';
      const minBalanceAmount = Number(localStorage.getItem('tabungan_minimumSaldoAmount')) || 10000;

      if (nom > saldo) {
        showAlert('Gagal! Saldo tidak mencukupi.', { type: 'danger', title: 'Saldo Tidak Cukup' });
        return;
      }

      if (isLimitEnabled && nom > limitAmount) {
        showAlert(`Gagal! Penarikan dibatasi maksimal Rp ${limitAmount.toLocaleString('id-ID')} / hari (Limit Penarikan Aktif).`, { type: 'danger', title: 'Limit Terlampaui' });
        return;
      }
      
      if (isMinBalanceEnabled && (saldo - nom < minBalanceAmount)) {
        showAlert(`Gagal! Sisa saldo mengendap di rekening tidak boleh kurang dari Rp ${minBalanceAmount.toLocaleString('id-ID')} (Minimum Saldo Aktif).`, { type: 'danger', title: 'Saldo Minimum' });
        return;
      }
    }

    if (trxType === 'SETOR' && targetWallet === 'TABUNGAN' && sumberDana === 'EWALLET') {
      const ewalletBalance = selectedStudent.saldoEWallet || 0;
      if (nom > ewalletBalance) {
        showAlert(
          `Gagal! Saldo E-Wallet siswa (Rp ${ewalletBalance.toLocaleString('id-ID')}) tidak mencukupi untuk top up tabungan sebesar Rp ${nom.toLocaleString('id-ID')}!`,
          { type: 'danger', title: 'Saldo E-Wallet Tidak Cukup' }
        );
        return;
      }
    }

    await onDepositOrWithdraw({
      siswaId: selectedStudent.id,
      tipe: trxType,
      nominal: nom,
      keterangan: keterangan || (sumberDana === 'EWALLET' ? 'Top Up Tabungan dari Saldo E-Wallet Siswa' : (targetWallet === 'EWALLET' ? 'Top Up Saldo E-Wallet' : 'Setoran Tabungan Siswa')),
      sumberDana: targetWallet === 'TABUNGAN' && trxType === 'SETOR' ? sumberDana : undefined,
    });
    setShowModal(false);
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-200">
      {/* Top Header Matching Document Page 13 */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-slate-900">Saldo Siswa & Rekening Tabungan</h2>
            <p className="text-xs text-slate-500">
              Pengelolaan dana tabungan siswa SDMT Ponorogo, mutasi setoran dan penarikan tunai
            </p>
          </div>

          {/* Stat Pill */}
          <div className="bg-sky-50 border border-sky-200 px-4 py-2 rounded-xl flex items-center gap-3">
            <Wallet className="w-5 h-5 text-sky-700" />
            <div>
              <p className="text-[10px] text-sky-800 font-semibold uppercase">Total Saldo Terhimpun</p>
              <p className="text-sm font-mono font-black text-sky-950">
                Rp {totalSaldoAll.toLocaleString('id-ID')}
              </p>
            </div>
          </div>
        </div>

        {/* Filter row */}
        <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-600">Filter Kelas:</span>
            <select
              value={filterKelas}
              onChange={(e) => setFilterKelas(e.target.value)}
              className="text-xs border border-slate-300 rounded-md px-3 py-1.5 bg-slate-50 focus:ring-1 focus:ring-sky-500 font-medium"
            >
              <option value="ALL">-- Semua Kelas --</option>
              {kelasList.map((k) => (
                <option key={k.id} value={k.nama}>
                  {k.nama}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Cari Siswa / NIS..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-300 rounded-md focus:ring-1 focus:ring-sky-500 w-52"
            />
          </div>
        </div>
      </div>

      {/* Main Table Exact from Document Page 13 */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden flex flex-col min-h-[400px]">
        <div className="overflow-x-auto flex-1">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold uppercase sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-3 w-12 text-center">No</th>
                <SortableHeader label="NIS" columnKey="nis" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Nama Siswa" columnKey="nama" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Kelas" columnKey="kelasNama" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Virtual Account" columnKey="noVA" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="font-mono" />
                <SortableHeader label="Saldo Tabungan (Rp)" columnKey="saldoTabungan" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-right" />
                <SortableHeader label="Saldo E-Wallet (Rp)" columnKey="saldoEWallet" currentSortBy={sortBy as string} currentSortDir={sortDir} onSort={handleSort} className="text-right" />
                <th className="py-2.5 px-4 text-center w-48">Aksi Transaksi Tabungan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredSiswa.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">
                    Tidak ada data tabungan siswa yang cocok
                  </td>
                </tr>
              ) : (
                filteredSiswa.map((s, idx) => (
                  <tr key={s.id} className="hover:bg-sky-50/50 transition">
                    <td className="py-2 px-3 text-center text-slate-500 font-mono">{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                    <td className="py-2 px-3 font-mono font-bold text-slate-800">{s.nis}</td>
                    <td className="py-2 px-4 font-semibold text-slate-900">{s.nama}</td>
                    <td className="py-2 px-3 text-slate-700">{s.kelasNama}</td>
                    <td className="py-2 px-4 font-mono font-semibold text-sky-800">{s.noVA}</td>
                    <td className="py-2 px-4 text-right font-mono font-bold text-emerald-700 text-sm">
                      Rp {(s.saldoTabungan || 0).toLocaleString('id-ID')}
                    </td>
                    <td className="py-2 px-4 text-right font-mono font-bold text-indigo-700 text-sm">
                      Rp {(s.saldoEWallet || 0).toLocaleString('id-ID')}
                    </td>
                    <td className="py-2 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleOpenAction(s, 'SETOR', 'TABUNGAN')}
                        className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-300 rounded font-semibold text-[11px] flex items-center gap-1 transition"
                      >
                        <ArrowDownCircle className="w-3 h-3 text-emerald-600" />
                        <span>Setor</span>
                      </button>
                      <button
                        onClick={() => handleOpenAction(s, 'TARIK', 'TABUNGAN')}
                        className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 rounded font-semibold text-[11px] flex items-center gap-1 transition"
                      >
                        <ArrowUpCircle className="w-3 h-3 text-amber-600" />
                        <span>Tarik</span>
                      </button>
                      <button
                        onClick={() => handleOpenAction(s, 'SETOR', 'EWALLET')}
                        className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 border border-indigo-300 rounded font-semibold text-[11px] flex items-center gap-1 transition"
                        title="Top Up E-Wallet Siswa"
                      >
                        <Wallet className="w-3 h-3 text-indigo-600" />
                        <span>Top Up</span>
                      </button>
                      <button
                        onClick={() => handleOpenPrint(s)}
                        className="px-2.5 py-1 bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-300 rounded font-semibold text-[11px] flex items-center gap-1 transition"
                      >
                        <Printer className="w-3 h-3 text-sky-600" />
                        <span>Cetak Buku</span>
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination UI */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-200">
            <span className="text-xs text-slate-500">
              Menampilkan {(currentPage - 1) * rowsPerPage + 1} -{' '}
              {Math.min(currentPage * rowsPerPage, totalRows)} dari {totalRows} siswa
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p: number) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed rounded text-xs font-semibold text-slate-700 transition"
              >
                Prev
              </button>
              <span className="text-xs font-semibold text-slate-700">
                Hal {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p: number) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed rounded text-xs font-semibold text-slate-700 transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Setor / Tarik Tabungan */}
      {showModal && selectedStudent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-slate-200 animate-in fade-in">
            <h3 className="text-sm font-bold text-slate-900 mb-3 pb-2 border-b">
              {trxType === 'SETOR' 
                ? (targetWallet === 'EWALLET' ? 'Top Up E-Wallet Siswa' : 'Setoran Tabungan Siswa') 
                : (targetWallet === 'EWALLET' ? 'Tarik Saldo E-Wallet' : 'Penarikan Tabungan Siswa')}
            </h3>

            <div className="bg-slate-50 p-3 rounded-md text-xs mb-4 space-y-1">
              <p>
                <span className="text-slate-500">Siswa:</span>{' '}
                <span className="font-bold text-slate-800">{selectedStudent.nama}</span> ({selectedStudent.nis})
              </p>
              <p>
                <span className="text-slate-500">
                  {targetWallet === 'EWALLET' ? 'Saldo E-Wallet Saat Ini:' : 'Saldo Tabungan Saat Ini:'}
                </span>{' '}
                <span className={`font-mono font-bold ${targetWallet === 'EWALLET' ? 'text-indigo-700' : 'text-emerald-700'}`}>
                  Rp {(targetWallet === 'EWALLET' ? selectedStudent.saldoEWallet : selectedStudent.saldoTabungan || 0)?.toLocaleString('id-ID')}
                </span>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 text-xs">
              {trxType === 'SETOR' && targetWallet === 'TABUNGAN' && (
                <div>
                  <label className="block text-slate-700 font-semibold mb-1.5">Sumber Dana Setoran:</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSumberDana('KASIR');
                        setKeterangan('Setoran Tabungan Siswa');
                      }}
                      className={`p-2.5 rounded-lg border text-left transition ${
                        sumberDana === 'KASIR'
                          ? 'border-emerald-500 bg-emerald-50/70 text-emerald-950 font-bold'
                          : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <p className="text-xs">💵 Tunai Kasir Loket</p>
                      <p className="text-[10px] text-slate-500 font-normal">Penerimaan fisik kas</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setSumberDana('EWALLET');
                        setKeterangan('Top Up Tabungan dari Saldo E-Wallet Siswa');
                      }}
                      className={`p-2.5 rounded-lg border text-left transition ${
                        sumberDana === 'EWALLET'
                          ? 'border-indigo-500 bg-indigo-50/70 text-indigo-950 font-bold'
                          : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <p className="text-xs">📱 E-Wallet Siswa</p>
                      <p className="text-[10px] text-indigo-700 font-semibold">
                        Saldo: Rp {(selectedStudent.saldoEWallet || 0).toLocaleString('id-ID')}
                      </p>
                    </button>
                  </div>
                  {sumberDana === 'EWALLET' && (
                    <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-[11px] text-indigo-900">
                      ℹ Sistem akan <strong>otomatis mendebit Saldo E-Wallet</strong> siswa dan menambah Saldo Tabungan secara bersamaan.
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Nominal (Rp)</label>
                <input
                  type="number"
                  placeholder="50000"
                  value={nominal}
                  onChange={(e) => setNominal(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md font-mono text-sm"
                  required
                />
                {trxType === 'SETOR' && targetWallet === 'TABUNGAN' && sumberDana === 'EWALLET' && Number(nominal) > (selectedStudent.saldoEWallet || 0) && (
                  <p className="text-[11px] text-rose-600 font-semibold mt-1">
                    ⚠ Saldo E-Wallet tidak mencukupi (Tersedia: Rp {(selectedStudent.saldoEWallet || 0).toLocaleString('id-ID')})
                  </p>
                )}
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Keterangan / Catatan</label>
                <input
                  type="text"
                  value={keterangan}
                  onChange={(e) => setKeterangan(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-3.5 py-1.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-md font-medium"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className={`px-5 py-1.5 text-white rounded-md font-semibold ${
                    trxType === 'SETOR' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-600 hover:bg-amber-500'
                  }`}
                >
                  {trxType === 'SETOR' ? 'Simpan Setoran' : 'Proses Penarikan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPrintModal && studentToPrint && (
        <CetakBukuTabunganModal
          siswa={studentToPrint}
          onClose={() => setShowPrintModal(false)}
        />
      )}
    </div>
  );
};
